import {
  useProcessCarriers,
  useProcessCountries,
  useProcessWarehouses,
  useSelectedEcommerceIds
} from '@/hooks'
import { useTranslation } from 'react-i18next'

type FilterOption = { label: string; value: string }

export const FILTER_KEYS = [
  'refund_status',
  'logistics_status',
  'compensation',
  'return_method',
  'country',
  'warehouse_id',
  'carrier'
] as const

export type FilterKey = (typeof FILTER_KEYS)[number]

type StaticOption = { i18nKey: string; value: string }

const STATIC_FILTERS = {
  refund_status: [
    { i18nKey: 'refund_status.pending', value: 'REFUND_STATUS_PENDING' },
    { i18nKey: 'refund_status.processing', value: 'REFUND_STATUS_PROCESSING' },
    { i18nKey: 'refund_status.completed', value: 'REFUND_STATUS_COMPLETED' },
    { i18nKey: 'refund_status.na', value: 'REFUND_STATUS_NA' }
  ],
  logistics_status: [
    {
      i18nKey: 'logistics_status.label_pending',
      value: 'LOGISTICS_STATUS_LABEL_PENDING'
    },
    {
      i18nKey: 'logistics_status.label_created',
      value: 'LOGISTICS_STATUS_CREATED'
    },
    {
      i18nKey: 'logistics_status.collected',
      value: 'LOGISTICS_STATUS_COLLECTED'
    },
    {
      i18nKey: 'logistics_status.warehouse',
      value: 'LOGISTICS_STATUS_WAREHOUSE'
    },
    { i18nKey: 'logistics_status.na', value: 'LOGISTICS_STATUS_NA' }
  ],
  compensation: [
    { i18nKey: 'refund_methods.ORIGINAL', value: 'ORIGINAL' },
    { i18nKey: 'refund_methods.BANK_TRANSFER', value: 'BANK_TRANSFER' },
    { i18nKey: 'refund_methods.PROMO_CODE', value: 'PROMO_CODE' },
    { i18nKey: 'refund_methods.GIFT_CARD', value: 'GIFT_CARD' },
    { i18nKey: 'refund_methods.EXCHANGE', value: 'EXCHANGE' }
  ],
  return_method: [
    { i18nKey: 'return_methods.HOME_PICK_UP', value: 'HOME_PICK_UP' },
    { i18nKey: 'return_methods.DROP_OFF_POINT', value: 'DROP_OFF_POINT' },
    { i18nKey: 'return_methods.DELIVERY_PICKUP', value: 'DELIVERY_PICKUP' },
    { i18nKey: 'return_methods.IN_STORE', value: 'IN_STORE' },
    { i18nKey: 'return_methods.SELF_SHIPPING', value: 'SELF_SHIPPING' },
    { i18nKey: 'return_methods.NO_RETURN_METHOD', value: 'NO_RETURN_METHOD' }
  ]
} satisfies Partial<Record<FilterKey, StaticOption[]>>

export type FilterConfig = {
  key: FilterKey
  options: FilterOption[]
  isLoading?: boolean
}

export const useProcessFilters = (): FilterConfig[] => {
  const { t } = useTranslation()
  const ecommerceIds = useSelectedEcommerceIds()

  const { data: countries = [], isLoading: isCountriesLoading } =
    useProcessCountries(ecommerceIds)

  const { data: carriers = [], isLoading: isCarriersLoading } =
    useProcessCarriers(ecommerceIds)

  const { data: warehouses = [], isLoading: isWarehousesLoading } =
    useProcessWarehouses(ecommerceIds, true)

  const translateStatic = (opts: StaticOption[]): FilterOption[] =>
    opts.map(({ i18nKey, value }) => ({ label: t(i18nKey), value }))

  return [
    {
      key: 'refund_status',
      options: translateStatic(STATIC_FILTERS.refund_status)
    },
    {
      key: 'logistics_status',
      options: translateStatic(STATIC_FILTERS.logistics_status)
    },
    {
      key: 'compensation',
      options: translateStatic(STATIC_FILTERS.compensation)
    },
    {
      key: 'return_method',
      options: translateStatic(STATIC_FILTERS.return_method)
    },
    {
      key: 'country',
      options: countries.map((c) => ({
        label: c.name?.toLowerCase() ?? c.iso ?? '',
        value: c.iso ?? ''
      })),
      isLoading: isCountriesLoading
    },
    {
      key: 'warehouse_id',
      options: warehouses.map((w) => ({
        label: [w.city, w.name].filter(Boolean).join(' - '),
        value: w.id ?? ''
      })),
      isLoading: isWarehousesLoading
    },
    {
      key: 'carrier',
      options: carriers.map((c) => ({ label: c.toUpperCase(), value: c })),
      isLoading: isCarriersLoading
    }
  ]
}
